import { Controller } from "@hotwired/stimulus";
import { loadPdfjs } from "../lib/pdfjs";
import {
  LARGE_ZOOM_STEP_PERCENT,
  ZOOM_STEP_PERCENT,
  fitScale,
  rotatedSize,
  stepZoom,
  turn,
} from "../lib/document_viewer_math";

// Most pixels any one canvas may hold. Safari refuses canvases past ~16.7M
// pixels, and a zoomed multi-page PDF would otherwise eat memory fast.
const MAX_CANVAS_PIXELS = 16_000_000;
const MAX_TOTAL_PIXELS = 64_000_000;

// Fills a shared Bootstrap modal with a preview of the document whose trigger
// opened it, with zoom and rotate controls. Images and PDFs both render to
// <canvas> (PDFs via PDF.js) so the controls behave the same for either. If
// PDF.js can't load the file, it falls back to the browser's own PDF viewer in
// an <iframe>, and the controls are disabled.
//
// Bootstrap handles show/hide via data-bs-toggle; we load on show, draw once
// the modal is laid out (shown), and tear everything down on hide.
export default class extends Controller {
  static targets = ["title", "body", "zoomLabel", "control", "openLink"];

  connect() {
    this.onShow = this.onShow.bind(this);
    this.onShown = this.onShown.bind(this);
    this.onHidden = this.onHidden.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.element.addEventListener("show.bs.modal", this.onShow);
    this.element.addEventListener("shown.bs.modal", this.onShown);
    this.element.addEventListener("hidden.bs.modal", this.onHidden);
    this.element.addEventListener("keydown", this.onKeydown);
    window.addEventListener("resize", this.onResize);
    this.session = 0;
  }

  disconnect() {
    this.element.removeEventListener("show.bs.modal", this.onShow);
    this.element.removeEventListener("shown.bs.modal", this.onShown);
    this.element.removeEventListener("hidden.bs.modal", this.onHidden);
    this.element.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("resize", this.onResize);
    this.teardown();
  }

  // --- Modal lifecycle -----------------------------------------------------

  onShow(event) {
    const trigger = event.relatedTarget;
    if (!trigger) return;

    this.teardown();
    const session = ++this.session;
    const { docUrl, docType, docName } = trigger.dataset;
    this.name = docName || "Document";
    this.url = docUrl;
    this.zoom = 1;
    this.rotation = 0;

    if (this.hasTitleTarget) this.titleTarget.textContent = this.name;
    if (this.hasOpenLinkTarget) this.openLinkTarget.href = docUrl;
    this.setControlsEnabled(false);
    this.updateZoomLabel();
    this.bodyTarget.replaceChildren(this.statusMessage("Loading…"));

    const load = (docType || "").startsWith("image/")
      ? this.loadImage(docUrl)
      : this.loadPdf(docUrl);

    load
      .then((source) => {
        if (session !== this.session) {
          source.destroy?.();
          return;
        }
        this.source = source;
        this.bodyTarget.replaceChildren();
        this.setControlsEnabled(true);
        this.render();
      })
      .catch((error) => {
        if (session !== this.session) return;
        console.warn("Document preview failed", error);
        this.showFallback(docUrl, docType);
      });
  }

  // Only draw once the modal is fully laid out: rendering mid-transition (the
  // body has no height yet) leaves it scrolled to the bottom when it appears.
  onShown() {
    this.visible = true;
    this.render();
  }

  onHidden() {
    this.teardown();
    this.bodyTarget.replaceChildren();
  }

  teardown() {
    this.session += 1;
    this.visible = false;
    this.renderTask?.cancel();
    this.renderTask = null;
    this.source?.destroy?.();
    this.source = null;
    clearTimeout(this.resizeTimer);
  }

  onResize() {
    if (!this.source) return;
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.render(), 150);
  }

  // = / - zoom 5%, with Shift (+ / _ on US keyboards) 25%; 0 fits; r / R rotate.
  // Ignored with Cmd/Ctrl/Alt, so the browser's own zoom keeps working.
  onKeydown(event) {
    if (!this.source || event.metaKey || event.ctrlKey || event.altKey) return;
    const actions = {
      "+": () => this.zoomIn(event),
      "=": () => this.zoomIn(event),
      "-": () => this.zoomOut(event),
      _: () => this.zoomOut(event),
      0: () => this.resetZoom(),
      r: () => this.rotateRight(),
      R: () => this.rotateLeft(),
    };
    const action = actions[event.key];
    if (!action) return;
    event.preventDefault();
    action();
  }

  // --- Toolbar actions -----------------------------------------------------

  // Shift (on the click or the key press) takes the large step.
  zoomIn(event) {
    this.setZoom(stepZoom(this.zoom, 1, this.zoomStep(event)));
  }

  zoomOut(event) {
    this.setZoom(stepZoom(this.zoom, -1, this.zoomStep(event)));
  }

  zoomStep(event) {
    return event?.shiftKey ? LARGE_ZOOM_STEP_PERCENT : ZOOM_STEP_PERCENT;
  }

  resetZoom() {
    this.setZoom(1);
  }

  rotateLeft() {
    this.setRotation(turn(this.rotation, -90));
  }

  rotateRight() {
    this.setRotation(turn(this.rotation, 90));
  }

  setZoom(zoom) {
    if (!this.source || zoom === this.zoom) return;
    this.zoom = zoom;
    this.updateZoomLabel();
    this.render();
  }

  setRotation(rotation) {
    if (!this.source) return;
    this.rotation = rotation;
    this.render();
  }

  // --- Loading -------------------------------------------------------------

  async loadImage(url) {
    const image = new Image();
    image.src = url;
    await image.decode();
    return { kind: "image", image };
  }

  async loadPdf(url) {
    const pdfjs = await loadPdfjs();
    const loadingTask = pdfjs.getDocument({ url });
    const pdf = await loadingTask.promise;
    const pages = [];
    for (let n = 1; n <= pdf.numPages; n += 1) {
      pages.push(await pdf.getPage(n));
    }
    return { kind: "pdf", pages, destroy: () => loadingTask.destroy() };
  }

  // --- Rendering -----------------------------------------------------------

  render() {
    if (!this.source || !this.visible) return;
    const width = this.viewerWidth();
    if (!width) return;

    // Keep the same spot centered across a zoom/rotate (but open at the top).
    const body = this.bodyTarget;
    const anchored = this.source.rendered;
    const anchorX =
      (body.scrollLeft + body.clientWidth / 2) / (body.scrollWidth || 1);
    const anchorY =
      (body.scrollTop + body.clientHeight / 2) / (body.scrollHeight || 1);

    if (this.source.kind === "image") {
      this.renderImage(width);
    } else {
      this.renderPdf(width);
    }
    this.source.rendered = true;

    if (anchored) {
      body.scrollLeft = anchorX * body.scrollWidth - body.clientWidth / 2;
      body.scrollTop = anchorY * body.scrollHeight - body.clientHeight / 2;
    } else {
      body.scrollTo(0, 0);
    }
  }

  renderImage(width) {
    const { image } = this.source;
    const natural = { width: image.naturalWidth, height: image.naturalHeight };
    const rotated = rotatedSize(natural.width, natural.height, this.rotation);
    const scale = fitScale(rotated.width, width, this.zoom);
    // Downsample only huge photos (e.g. 48MP phone shots) to stay under the cap.
    const density = Math.min(
      1,
      Math.sqrt(MAX_CANVAS_PIXELS / (natural.width * natural.height)),
    );

    const canvas = this.canvasAt(0, 1);
    canvas.width = Math.floor(rotated.width * density);
    canvas.height = Math.floor(rotated.height * density);
    canvas.style.width = `${rotated.width * scale}px`;
    canvas.style.height = `${rotated.height * scale}px`;
    canvas.setAttribute("aria-label", this.name);

    const context = canvas.getContext("2d");
    context.translate(canvas.width / 2, canvas.height / 2);
    context.rotate((this.rotation * Math.PI) / 180);
    context.scale(density, density);
    context.drawImage(image, -natural.width / 2, -natural.height / 2);
  }

  renderPdf(width) {
    const { pages } = this.source;
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    const perPageBudget = Math.min(
      MAX_CANVAS_PIXELS,
      MAX_TOTAL_PIXELS / pages.length,
    );

    // Size every page synchronously (so the scroll anchor lands correctly),
    // then paint them one after another.
    const jobs = pages.map((page, index) => {
      const rotation = turn(page.rotate, this.rotation);
      const base = page.getViewport({ scale: 1, rotation });
      const cssScale = fitScale(base.width, width, this.zoom);
      const cssWidth = base.width * cssScale;
      const cssHeight = base.height * cssScale;
      const density = Math.min(
        pixelRatio,
        Math.sqrt(perPageBudget / (cssWidth * cssHeight)),
      );

      const canvas = this.canvasAt(index, pages.length);
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;
      canvas.setAttribute(
        "aria-label",
        `${this.name}, page ${index + 1} of ${pages.length}`,
      );

      return {
        page,
        canvas,
        viewport: page.getViewport({ scale: cssScale * density, rotation }),
      };
    });

    const session = this.session;
    this.paintPdf(jobs, session).catch((error) => {
      if (session !== this.session) return;
      console.warn("PDF page render failed", error);
      this.showFallback(this.url, "application/pdf");
    });
  }

  async paintPdf(jobs, session) {
    this.renderTask?.cancel();
    const paintId = (this.paintId = (this.paintId || 0) + 1);

    for (const { page, canvas, viewport } of jobs) {
      // Draw offscreen, then swap in, so pages don't blank out while re-rendering.
      const fresh = document.createElement("canvas");
      fresh.width = Math.floor(viewport.width);
      fresh.height = Math.floor(viewport.height);

      this.renderTask = page.render({
        canvasContext: fresh.getContext("2d"),
        viewport,
      });
      try {
        await this.renderTask.promise;
      } catch (error) {
        if (error?.name === "RenderingCancelledException") return;
        throw error;
      }
      if (session !== this.session || paintId !== this.paintId) return;

      canvas.width = fresh.width;
      canvas.height = fresh.height;
      canvas.getContext("2d").drawImage(fresh, 0, 0);
    }
    this.renderTask = null;
  }

  // The canvas for page `index`, creating it (and dropping extras) as needed.
  canvasAt(index, count) {
    const canvases = this.bodyTarget.querySelectorAll("canvas");
    for (let i = count; i < canvases.length; i += 1) canvases[i].remove();
    if (canvases[index]) return canvases[index];

    const canvas = document.createElement("canvas");
    canvas.setAttribute("role", "img");
    canvas.className = "d-block mx-auto mb-3 bg-white shadow-sm";
    this.bodyTarget.appendChild(canvas);
    return canvas;
  }

  viewerWidth() {
    const style = getComputedStyle(this.bodyTarget);
    return (
      this.bodyTarget.clientWidth -
      parseFloat(style.paddingLeft) -
      parseFloat(style.paddingRight)
    );
  }

  // --- UI state ------------------------------------------------------------

  updateZoomLabel() {
    if (!this.hasZoomLabelTarget) return;
    this.zoomLabelTarget.textContent =
      this.zoom === 1 ? "Fit" : `${Math.round(this.zoom * 100)}%`;
  }

  setControlsEnabled(enabled) {
    this.controlTargets.forEach((control) => {
      control.disabled = !enabled;
    });
  }

  statusMessage(text) {
    const p = document.createElement("p");
    p.className = "text-muted text-center my-5";
    p.textContent = text;
    return p;
  }

  // PDF.js (or the image) couldn't load: hand PDFs to the browser's built-in
  // viewer, and say plainly that the controls are off.
  showFallback(url, type) {
    this.source?.destroy?.();
    this.source = null;
    this.setControlsEnabled(false);
    if ((type || "").startsWith("image/")) {
      this.bodyTarget.replaceChildren(
        this.statusMessage(
          "This image couldn't be displayed. Try opening it in a new tab.",
        ),
      );
      return;
    }

    const note = this.statusMessage(
      "Zoom and rotate aren't available for this file, so it's shown in the browser's PDF viewer.",
    );
    note.classList.replace("my-5", "mb-2");
    const iframe = document.createElement("iframe");
    iframe.src = url;
    iframe.title = this.name;
    iframe.style.width = "100%";
    iframe.style.height = "70vh";
    iframe.style.border = "0";
    this.bodyTarget.replaceChildren(note, iframe);
  }
}
